import {
  AgentCloudTestCreationType,
  AgentTestOperation,
} from '@elementary/common-ui/types';
import { ColumnSchema, ConfigFieldKey } from './types';

export const SENSITIVITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const ANOMALY_DIRECTION_OPTIONS = [
  { value: 'BOTH', label: 'Both' },
  { value: 'SPIKE', label: 'Spike' },
  { value: 'DROP', label: 'Drop' },
];

export const SEVERITY_OPTIONS = [
  { value: 'WARNING', label: 'Warning' },
  { value: 'ERROR', label: 'Error' },
];

export const BASELINE_MODE_OPTIONS = [
  { value: 'complete', label: 'Complete' },
  { value: 'manual', label: 'Manual' },
];

export const ENFORCEMENT_LEVEL_OPTIONS = [
  { value: 'all', label: 'All changes' },
  { value: 'breaking', label: 'Breaking changes only' },
  { value: 'downstream', label: 'Downstream impact only' },
  { value: 'downstream_critical', label: 'Critical downstream impact only' },
];

export const TEST_TYPE_FIELDS: Record<
  AgentCloudTestCreationType,
  ConfigFieldKey[]
> = {
  freshness: [
    'sensitivity',
    'threshold',
    'trainingPeriod',
    'excludedWeeklyTimeRanges',
    'failureThresholdGrace',
    'severity',
    'owners',
    'tags',
  ],
  volume: [
    'sensitivity',
    'anomalyDirection',
    'detectionPeriod',
    'trainingPeriod',
    'excludedTimeRanges',
    'spikeThreshold',
    'dropThreshold',
    'severity',
    'owners',
    'tags',
  ],
  data_contract: [
    'baselineMode',
    'baseline',
    'enforcementLevel',
    'severity',
    'owners',
    'tags',
  ],
  pipeline_task_performance: [
    'sensitivity',
    'anomalyDirection',
    'trainingPeriod',
    'excludedTimeRanges',
    'fixedThreshold',
    'severity',
    'owners',
    'tags',
  ],
};

export const TEST_TYPE_NAMES: Record<AgentCloudTestCreationType, string> = {
  freshness: 'freshness_anomalies',
  volume: 'volume_anomalies',
  data_contract: 'data_contract',
  pipeline_task_performance: 'pipeline_task_performance',
};

export const TEST_TYPE_SUB_TYPES: Record<AgentCloudTestCreationType, string> = {
  freshness: 'freshness',
  volume: 'content',
  data_contract: 'schema',
  pipeline_task_performance: 'pipeline',
};

export const BREADCRUMB_LABELS: Record<AgentTestOperation, string> = {
  update: 'Changes Preview',
  create: 'Creation Preview',
  delete: 'Deletion Preview',
};

const asColumnSchema = <T extends ColumnSchema[]>(columns: [...T]): [...T] =>
  columns;

export const COLUMN_SCHEMA = asColumnSchema([
  {
    type: 'dropdown',
    key: 'sensitivity',
    id: 'sensitivity_change',
    header: 'Sensitivity',
    options: SENSITIVITY_OPTIONS,
  },
  {
    type: 'period',
    key: 'threshold',
    id: 'threshold_change',
    header: 'Threshold',
    nullable: true,
  },
  {
    type: 'dropdown',
    key: 'anomalyDirection',
    id: 'anomaly_direction_change',
    header: 'Anomaly direction',
    options: ANOMALY_DIRECTION_OPTIONS,
  },
  {
    type: 'period',
    key: 'detectionPeriod',
    id: 'detection_period_change',
    header: 'Detection period',
  },
  {
    type: 'number',
    key: 'spikeThreshold',
    id: 'spike_threshold_change',
    header: 'Spike threshold',
    suffix: '%',
  },
  {
    type: 'number',
    key: 'dropThreshold',
    id: 'drop_threshold_change',
    header: 'Drop threshold',
    suffix: '%',
  },
  {
    type: 'period',
    key: 'trainingPeriod',
    id: 'training_period_change',
    header: 'Training period',
  },
  {
    type: 'period',
    key: 'failureThresholdGrace',
    id: 'failure_threshold_grace_change',
    header: 'Failure grace',
    nullable: true,
  },
  {
    type: 'period',
    key: 'fixedThreshold',
    id: 'fixed_threshold_change',
    header: 'Fixed threshold',
  },
  {
    type: 'timeRanges',
    key: 'excludedWeeklyTimeRanges',
    id: 'excluded_weekly_time_ranges_change',
    header: 'Excluded weekly time ranges',
  },
  {
    type: 'timeRanges',
    key: 'excludedTimeRanges',
    id: 'excluded_time_ranges_change',
    header: 'Excluded time ranges',
  },
  {
    type: 'dropdown',
    key: 'severity',
    id: 'severity_change',
    header: 'Severity',
    options: SEVERITY_OPTIONS,
  },
  {
    type: 'dropdown',
    key: 'baselineMode',
    id: 'baseline_mode_change',
    header: 'Baseline mode',
    options: BASELINE_MODE_OPTIONS,
  },
  {
    type: 'baselineColumns',
    key: 'baseline',
    id: 'baseline_columns_change',
    header: 'Baseline columns',
  },
  {
    type: 'dropdown',
    key: 'enforcementLevel',
    id: 'enforcement_level_change',
    header: 'Enforcement level',
    options: ENFORCEMENT_LEVEL_OPTIONS,
  },
  {
    type: 'list',
    key: 'owners',
    id: 'owners_change',
    header: 'Owners',
    itemLabel: ['owner', 'owners'],
    optionsKey: 'owners',
  },
  {
    type: 'list',
    key: 'tags',
    id: 'tags_change',
    header: 'Tags',
    itemLabel: ['tag', 'tags'],
    optionsKey: 'tags',
  },
  {
    type: 'boolean',
    key: 'enabled',
    id: 'enabled_change',
    header: 'Test enabled',
    trueLabel: 'Enable Test',
    falseLabel: 'Disable Test',
  },
  {
    type: 'boolean',
    key: 'muted',
    id: 'muted_change',
    header: 'Alerts muted',
    trueLabel: 'Mute Alerts',
    falseLabel: 'Unmute Alerts',
  },
]);

// Compile-time exhaustiveness: every AgentCloudTestConfigFields key must have
// a COLUMN_SCHEMA entry. Adding a typed field without a column fails here.
type ColumnSchemaKeys = typeof COLUMN_SCHEMA[number]['key'];
type MissingColumnSchemaKeys = Exclude<ConfigFieldKey, ColumnSchemaKeys>;
type AssertColumnSchemaCoversAllConfigFields =
  MissingColumnSchemaKeys extends never ? true : MissingColumnSchemaKeys;
const _assertColumnSchemaCoversAllConfigFields: AssertColumnSchemaCoversAllConfigFields =
  true;
void _assertColumnSchemaCoversAllConfigFields;

export const KNOWN_CONFIG_FIELD_KEYS: ReadonlySet<string> = new Set(
  COLUMN_SCHEMA.map((schema) => schema.key),
);

export const STORAGE_KEY = 'agent_config_preview_table';
